import React, { useState, useEffect } from "react";

const CountryDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Select Country",
  className = "",
  required = false,
  id = "country",
  name = "country"
}) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      // restcountries.com now 301-redirects to a non-CORS host, so we use
      // countriesnow.space instead (CORS-enabled; also used by StateDropdown).
      // /iso gives name + ISO codes; /codes gives the dialing (phone) code.
      const [isoRes, codesRes] = await Promise.all([
        fetch('https://countriesnow.space/api/v0.1/countries/iso'),
        fetch('https://countriesnow.space/api/v0.1/countries/codes'),
      ]);

      if (!isoRes.ok) {
        throw new Error(`Failed to fetch countries: ${isoRes.status}`);
      }

      const isoJson = await isoRes.json();
      // Build a name -> dialing code map (best-effort; phone codes are optional).
      const phoneByName = {};
      if (codesRes.ok) {
        const codesJson = await codesRes.json();
        (codesJson.data || []).forEach((c) => {
          if (c.name) phoneByName[c.name] = c.dial_code || '';
        });
      }

      // Sort countries alphabetically and format data to the same shape as before
      // ({ name, code, phoneCode, flag }) so consumers need no changes.
      const formattedCountries = (isoJson.data || [])
        .map((country) => ({
          name: country.name || 'Unknown',
          code: country.Iso2 || '',
          phoneCode: phoneByName[country.name] || '',
          flag: '',
        }))
        .filter((country) => country.name !== 'Unknown')
        .sort((a, b) => a.name.localeCompare(b.name));

      setCountries(formattedCountries);
      setError(null);
    } catch (err) {
      console.error('Error fetching countries:', err);
      setError('Failed to load countries. Please try again later.');
      
      // Fallback to basic list if API fails
      setCountries([
        { name: "India", code: "IN", phoneCode: "+91", flag: "" },
        { name: "United States", code: "US", phoneCode: "+1", flag: "" },
        { name: "United Kingdom", code: "GB", phoneCode: "+44", flag: "" },
        { name: "Canada", code: "CA", phoneCode: "+1", flag: "" },
        { name: "Australia", code: "AU", phoneCode: "+61", flag: "" }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const selectedCountry = countries.find((c) => c.name === e.target.value);
    if (onChange && selectedCountry) {
      onChange(selectedCountry);
    }
  };

  if (loading) {
    return (
      <select 
        className={className}
        disabled
        id={id}
        name={name}
      >
        <option>Loading countries...</option>
      </select>
    );
  }

  if (error && countries.length === 0) {
    return (
      <select 
        className={className}
        disabled
        id={id}
        name={name}
      >
        <option>Error loading countries</option>
      </select>
    );
  }

  return (
    <select
      id={id}
      name={name}
      value={value}
      onChange={handleChange}
      className={className}
      required={required}
      style={{ 
        height: '36px',
        padding: '0 8px',
        lineHeight: '36px'
      }}
    >
      {placeholder && !value && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {countries.map((country) => (
        <option key={country.code} value={country.name} style={{ padding: '10px 8px', lineHeight: '1.5' }}>
          {country.name}
        </option>
      ))}
    </select>
  );
};

export default CountryDropdown;
